/**
 * ItemGroupStorefrontSection — custom widget for the "Storefront" section on Item Group.
 *
 * Follows the same iPad Settings pattern as Item's StorefrontSection:
 *   - Toggle row saves inline (optimistic)
 *   - Auto-slug generation when toggling ON with empty slug
 *   - Slug row opens edit modal for manual override
 *   - Dimmed state when storefront is OFF
 */

import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { Switch } from "@/components/ui/switch";
import { useUpdateDoc } from "@/lib/hooks/useFrappeQuery";
import { useAuthStore } from "@/lib/store/authStore";
import { toast } from "sonner";
import { slugify, displaySlug } from "../../item/widgets/storefrontUtils";
import { CARD_CLICKABLE } from "@/config/design-tokens";
import type { ShopifySectionWidgetProps } from "../../item/types";
import { SectionHeader } from "../../../SectionHeader";
import { useDetailDensity } from "../../../../hooks/useDetailDensity";
import { ItemGroupSlugEditModal } from "./ItemGroupSlugEditModal";

const STOREFRONT_FALLBACK_URL = "yourstore.shop.zaviago.com";

export function ItemGroupStorefrontSection({
  formData,
  onFieldChange,
}: ShopifySectionWidgetProps) {
  const docName = formData?.name as string | undefined;
  const storefrontUrl = useAuthStore((s) => s.storefrontUrl);
  const baseUrl = storefrontUrl || STOREFRONT_FALLBACK_URL;
  const { textSize, rowPx, rowPy, rowFirst, rowGap, dividerMx, rowLabel } = useDetailDensity();

  const enabled = formData?.storefront_enabled === 1 || formData?.storefront_enabled === true;
  const [localEnabled, setLocalEnabled] = useState(enabled);

  useEffect(() => {
    setLocalEnabled(enabled);
  }, [enabled]);

  const { mutateAsync, isPending } = useUpdateDoc("Item Group", docName || "");

  const [modalOpen, setModalOpen] = useState(false);

  const slug = (formData?.storefront_slug as string) || "";

  const handleToggle = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();

    if (!docName) return;

    const prev = localEnabled;
    const next = !localEnabled;
    setLocalEnabled(next);

    try {
      const payload: Record<string, any> = {
        storefront_enabled: next ? 1 : 0,
      };

      let autoSlug: string | null = null;
      if (next && !slug) {
        const groupName = (formData?.item_group_name as string) || "";
        if (groupName) {
          autoSlug = slugify(groupName);
          payload.storefront_slug = autoSlug;
        }
      }

      await mutateAsync(payload);

      onFieldChange("storefront_enabled", next ? 1 : 0);
      if (autoSlug) {
        onFieldChange("storefront_slug", autoSlug);
      }
    } catch {
      setLocalEnabled(prev);
      toast.error("Failed to update storefront visibility. Please try again.");
    }
  };

  const handleCardClick = () => {
    setModalOpen(true);
  };

  return (
    <>
      <div>
        <SectionHeader label="Storefront" textSize={textSize} />

        <div
          className={CARD_CLICKABLE}
          onClick={handleCardClick}
        >
          <div className="space-y-0">
            {/* Row 1: Toggle */}
            <div className={cn(`flex items-center ${rowGap}`, rowPx, rowFirst)}>
              <span className={cn(rowLabel, "w-1/2 text-foreground")}>
                Publish to Storefront
              </span>
              <div className={cn(rowLabel, "w-1/2 flex items-center justify-end")}>
                <div onClick={(e) => e.stopPropagation()}>
                  <Switch
                    checked={localEnabled}
                    onCheckedChange={() => {}}
                    onClick={handleToggle}
                    disabled={isPending || !docName}
                    aria-label="Publish to storefront"
                  />
                </div>
              </div>
            </div>

            <div className={cn(dividerMx, "border-t border-border/40")} />

            {/* Row 2: URL Slug */}
            <div
              className={cn(
                rowPx, rowPy, "transition-opacity",
                localEnabled ? "" : "opacity-50"
              )}
            >
              <div className={`flex items-center ${rowGap}`}>
                <span className={cn(rowLabel, "w-1/2 text-foreground")}>
                  URL Slug
                </span>
                <div className={cn(rowLabel, "w-1/2 text-right text-foreground break-words")}>
                  {slug ? displaySlug(slug) : <span className="text-muted-foreground">{"\u2014"}</span>}
                </div>
              </div>
              {slug && (
                <p className="mt-1 text-xs font-mono text-muted-foreground truncate">
                  {baseUrl}/collections/{displaySlug(slug)}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {docName && (
        <ItemGroupSlugEditModal
          open={modalOpen}
          onOpenChange={setModalOpen}
          currentSlug={slug}
          storefrontBaseUrl={baseUrl}
          docName={docName}
          onFieldChange={onFieldChange}
        />
      )}
    </>
  );
}
